import { useEffect, useMemo, useState, type ComponentType } from "react";
import { Outlet, useRouterState } from "@tanstack/react-router";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useTranslation } from "react-i18next";
import { PageLoader } from "@andrum/ui";
import { BottomNavigation } from "./BottomNavigation";
import { durations, easings } from "@/lib/motion/tokens";
import { useExperimentStore } from "@/stores/experiment-store";
import { TodayScreen } from "@/features/today/screens/TodayScreen";
import { ExploreScreen } from "@/features/explore/screens/ExploreScreen";
import { CoachScreen } from "@/features/coach/screens/CoachScreen";
import { InsightsScreen } from "@/features/insights/screens/InsightsScreen";
import { ProfileScreen } from "@/features/profile/screens/ProfileScreen";

const TAB_ROUTES: Array<{
  path: string;
  Component: ComponentType;
}> = [
  { path: "/app/today", Component: TodayScreen },
  { path: "/app/explore", Component: ExploreScreen },
  { path: "/app/coach", Component: CoachScreen },
  { path: "/app/insights", Component: InsightsScreen },
  { path: "/app/profile", Component: ProfileScreen },
];

function isTabPath(pathname: string): boolean {
  return TAB_ROUTES.some((t) => t.path === pathname);
}

function tabTransition(reduce: boolean | null) {
  if (reduce) return { duration: 0 };
  return {
    duration: durations.normal,
    ease: easings.enter,
  };
}

/**
 * Keep-alive tabs + soft stack transitions.
 * Content area is explicitly inset for Dynamic Island + bottom nav
 * so absolute panes never sit under chrome.
 */
export function AppShell() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const reduce = useReducedMotion();
  const hydrate = useExperimentStore((s) => s.hydrate);
  const hydrated = useExperimentStore((s) => s.hydrated);

  const [visited, setVisited] = useState<Set<string>>(() => {
    const initial = new Set<string>();
    if (isTabPath(pathname)) initial.add(pathname);
    else initial.add("/app/today");
    return initial;
  });

  useEffect(() => {
    if (!hydrated) void hydrate();
  }, [hydrate, hydrated]);

  useEffect(() => {
    const warm = () => {
      void import("@/features/explore/screens/ExploreScreen");
      void import("@/features/coach/screens/CoachScreen");
      void import("@/features/insights/screens/InsightsScreen");
      void import("@/features/profile/screens/ProfileScreen");
      if (!useExperimentStore.getState().hydrated) {
        void useExperimentStore.getState().hydrate();
      }
    };
    const id = window.setTimeout(warm, 280);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => {
    if (isTabPath(pathname)) {
      setVisited((prev) => {
        if (prev.has(pathname)) return prev;
        const next = new Set(prev);
        next.add(pathname);
        return next;
      });
    }
  }, [pathname]);

  const onTab = isTabPath(pathname);
  const stackKey = onTab ? null : pathname;

  const tabPanes = useMemo(
    () =>
      TAB_ROUTES.map(({ path, Component }) => {
        const active = pathname === path;
        const mounted = visited.has(path) || active;
        if (!mounted) return null;
        return (
          <motion.div
            key={path}
            className="absolute inset-0 min-h-0 overflow-hidden"
            initial={false}
            animate={{
              opacity: active ? 1 : 0,
              y: active ? 0 : 8,
              scale: active ? 1 : 0.992,
            }}
            transition={tabTransition(reduce)}
            style={{
              pointerEvents: active ? "auto" : "none",
              zIndex: active ? 2 : 1,
            }}
            aria-hidden={!active}
          >
            <Component />
          </motion.div>
        );
      }),
    [pathname, visited, reduce]
  );

  return (
    <div className="relative flex h-dvh max-h-dvh flex-col overflow-hidden bg-transparent">
      {/*
        Content host: NOT using padding + absolute (absolute ignores padding).
        Instead explicit top/bottom inset for safe area + floating dock.
      */}
      <main
        className="relative min-h-0 flex-1 overflow-hidden"
        style={{
          paddingTop: "max(env(safe-area-inset-top), 12px)",
          paddingBottom:
            "calc(var(--bottom-navigation-height) + var(--bottom-navigation-gap) + max(env(safe-area-inset-bottom), 8px) + 8px)",
        }}
      >
        <div className="relative h-full min-h-0 w-full">
          <div className="absolute inset-0 min-h-0">{tabPanes}</div>

          <AnimatePresence mode="sync" initial={false}>
            {stackKey ? (
              <motion.div
                key={stackKey}
                className="absolute inset-0 z-10 min-h-0 overflow-hidden bg-background/95 backdrop-blur-md"
                {...(reduce
                  ? { initial: false, animate: { opacity: 1, x: 0 } }
                  : {
                      initial: { opacity: 0, x: 18 },
                      animate: { opacity: 1, x: 0 },
                      exit: { opacity: 0, x: -12 },
                      transition: {
                        duration: durations.normal,
                        ease: easings.enter,
                      },
                    })}
              >
                <Outlet />
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>
      </main>
      <BottomNavigation />
    </div>
  );
}

export function TabRouteSlot() {
  return null;
}

export function RoutePending() {
  const { t } = useTranslation();
  return (
    <div className="flex h-dvh items-center justify-center overflow-hidden bg-background">
      <PageLoader label={t("loading")} />
    </div>
  );
}
